/**
 * The console's API error codes in plain sentences (docs/api-tenant-ops.md §10, docs/03-auth.md). The code itself stays
 * on screen as a small chip — engineers search for it — but the sentence comes first. An unknown code falls back to
 * the server's own message.
 */
const SENTENCES: Record<string, string> = {
  VALIDATION_ERROR: 'Some details are not valid.',
  ENVIRONMENT_NOT_FOUND: 'This environment no longer exists.',
  ENVIRONMENT_EXISTS: 'An environment with this key already exists.',
  ENVIRONMENT_DISABLED: 'This environment is switched off.',
  IMPORT_KEY_NOT_FOUND: 'This collector key no longer exists.',
  PROVISIONING_JOB_EXISTS: 'A setup for this tenant already exists — open it and retry it instead.',
  OWNER_EMAIL_TAKEN: "The owner's email already has an account, so no tenant was created. Use another email.",
  PROVISIONING_JOB_FINISHED: 'This setup has already finished.',
  PROVISIONING_JOB_CANCELLED: 'This setup was cancelled; create the tenant again.',
  PROVISIONING_STEP_RUNNING: 'A step is running right now; try again in a moment.',
  PROVISIONING_NOTHING_TO_RETRY: 'Nothing needs retrying.',
  PROVISIONING_EARLIER_STEP_FAILED: 'An earlier step stopped — retry that one first, or retry the whole setup.',
  SECOND_OWNER_REQUIRED: 'Deleting a tenant needs a second owner to approve it. Add another owner under Settings ▸ Admin users first.',
  SAME_ADMIN: 'Another owner must approve — not the one who asked for the deletion.',
  TENANT_REMOVAL_EXISTS: 'A deletion or clean-up of this tenant is already running — open it from the setup jobs.',
  REMOVAL_NOT_WAITING: 'This deletion is not waiting for that decision right now.',
  DB_SHORT_MISMATCH: "That is not the tenant's short name — type it exactly.",
  REMOVAL_USE_REJECT: 'A deletion is stopped with “Stop the deletion”, which brings a frozen tenant back online first.',
  REMOVAL_IN_PROGRESS: 'The clean-up has started and can no longer be cancelled. Retry it if it failed.',
  REMOVAL_IRREVERSIBLE: "The tenant's registry is already being removed; the deletion can no longer be stopped.",
  PROVISIONING_REMOVAL_EXPIRED: 'This deletion was not confirmed in time (or was stopped) and the tenant is online again. Cancel it and ask again.',
  PROVISIONING_REMOVAL_FINISHED: 'The tenant has been removed; there is nothing to retry.',
  TENANT_DEPROVISIONED: 'IAM reports this tenant as already removed.',
  PROVISIONING_JOB_NOT_FOUND: 'This setup no longer exists.',
  PROVISIONING_STEP_NOT_FOUND: 'This step no longer exists.',
  INDUSTRY_PACK_NOT_FOUND: 'This industry is not available in this console version.',
  INDUSTRY_PACK_INVALID: 'This industry has errors and cannot be used.',
  INDUSTRY_PACK_RETIRED: 'This industry has been replaced; choose one of the current ones.',
  REASON_REQUIRED: 'A reason is required for this change.',
  ADMIN_ACTOR_REQUIRED: 'The service did not receive who made the change; sign out and in again.',
  STEP_CATALOG_EMPTY: 'This environment has no setup steps configured.',
  PASSWORD_NOT_AVAILABLE: 'There is no generated password to reveal.',
  PASSWORD_ALREADY_REVEALED: 'The password was already revealed once and is gone.',
  TENANT_NOT_FOUND: 'This tenant is not on this environment.',
  PROXY_ROUTE_NOT_ALLOWED: 'The console does not allow this action on the service.',
  PAYLOAD_TOO_LARGE: 'The request is too large.',
  PLATFORM_UNAVAILABLE: 'The service could not be reached.',
  ADMIN_EMAIL_TAKEN: 'An admin with this e-mail already exists.',
  ADMIN_NOT_FOUND: 'This admin no longer exists.',
  ADMIN_SELF_CHANGE: 'You cannot change your own role, disable yourself or reset your own password here.',
  ADMIN_LAST_OWNER: 'This is the last owner — make someone else an owner first.',
  CURRENT_PASSWORD_WRONG: 'The current password is wrong.',
  PASSWORD_TOO_WEAK: 'The new password is too weak.',
  PASSWORD_CHANGE_REQUIRED: 'Choose a new password before doing anything else.',
  ACCOUNT_LOCKED: 'Too many wrong passwords — try again later.',
};

export function plainError(code: string | null | undefined): string | null {
  return code ? SENTENCES[code] ?? null : null;
}

/** The callout's heading for a refused call, by HTTP status. */
export function refusalTitle(status: number): string {
  if (status === 401) return 'Your session ended';
  if (status === 403) return 'Not permitted for your role';
  if (status === 404) return 'Not found';
  if (status === 409) return 'Conflicts with what already exists';
  if (status === 429) return 'Too many attempts';
  if (status === 400 || status === 422) return 'Not accepted';
  return `Refused (${status})`;
}
